import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useLanguage } from "@/context/LanguageContext";
import { toast } from "sonner";
import { Database, Upload, MapPin, Loader2 } from "lucide-react";

export default function DataImportPanel() {
    const { tr } = useLanguage();
    const [targets, setTargets] = useState([]);
    const [spatialLayers, setSpatialLayers] = useState([]);
    const [target, setTarget] = useState("applications");
    const [file, setFile] = useState(null);
    const [preview, setPreview] = useState(null);
    const [result, setResult] = useState(null);
    const [batches, setBatches] = useState([]);
    const [busy, setBusy] = useState(false);
    const [gisLayer, setGisLayer] = useState("parcels");
    const [gisFile, setGisFile] = useState(null);
    const [gisResult, setGisResult] = useState(null);
    const fileInputRef = useRef(null);
    const gisInputRef = useRef(null);

    const loadBatches = () => api.get("/admin/import/batches").then(({ data }) => setBatches(data)).catch(() => {});
    useEffect(() => {
        api.get("/admin/import/targets").then(({ data }) => { setTargets(data.targets); setSpatialLayers(data.spatial_layers); }).catch(() => {});
        loadBatches();
    }, []);

    const current = targets.find((t) => t.key === target);

    const doPreview = async () => {
        if (!file) return;
        setBusy(true); setResult(null);
        try {
            const fd = new FormData();
            fd.append("target", target); fd.append("file", file);
            const { data } = await api.post("/admin/import/preview", fd);
            setPreview(data);
        } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
    };

    const doImport = async () => {
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append("target", target); fd.append("file", file);
            const { data } = await api.post("/admin/import/execute", fd);
            setResult(data); setPreview(null); setFile(null);
            if (fileInputRef.current) fileInputRef.current.value = "";
            toast.success(`${data.imported} ${tr("records imported")}`);
            loadBatches();
        } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
    };

    const doSpatial = async () => {
        if (!gisFile) return;
        setBusy(true); setGisResult(null);
        try {
            const fd = new FormData();
            fd.append("layer", gisLayer); fd.append("file", gisFile);
            const { data } = await api.post("/admin/import/spatial", fd);
            setGisResult(data); setGisFile(null);
            if (gisInputRef.current) gisInputRef.current.value = "";
            toast.success(`${data.imported} ${tr("features imported")}`);
            loadBatches();
        } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
    };

    return (
        <div className="bg-card rounded-[18px] border border-slate-200/70 dark:border-white/10 p-6 lg:col-span-3" data-testid="config-data-import">
            <h3 className="font-heading font-bold text-navy dark:text-white flex items-center gap-2"><Database className="w-4 h-4 text-[#0092B0]" /> {tr("Data import & legacy conversion")}</h3>
            <p className="text-[11px] text-slate-400 mt-1 mb-4">{tr("Bulk-load PlanTrack / AMANDA exports (tab or comma delimited, header row required; PlanTrack extended '$table pk' headers supported) and spatial GeoJSON layers. Imported records are tagged with a batch id and appear throughout the platform, and every imported record carries a legacy marker identifying it as coming from the old desktop system.")}</p>
            <div className="grid lg:grid-cols-2 gap-5">
                <div>
                    <div className="flex gap-2 flex-wrap mb-3" data-testid="import-target-list">
                        {targets.map((t) => (
                            <button key={t.key} data-testid={`import-target-${t.key}`} onClick={() => { setTarget(t.key); setPreview(null); setResult(null); }}
                                className={`px-3 py-1.5 rounded-full text-[11px] font-bold transition-colors ${target === t.key ? "bg-[#0A2540] text-white" : "bg-slate-100 dark:bg-white/10 text-slate-500 hover:bg-slate-200"}`}>{t.label}</button>
                        ))}
                    </div>
                    {current && (
                        <div className="text-[11px] text-slate-400 mb-2.5">
                            <span className="font-bold text-slate-500">{tr("Expected columns")}:</span> <span className="font-data">{current.columns.join(", ")}</span>
                            {current.preset && <span className="ml-2 text-[#B8860B] font-bold">{tr("PlanTrack/AMANDA column preset auto-applied")}</span>}
                        </div>
                    )}
                    <div className="flex items-center gap-2">
                        <input type="file" accept=".csv,.tsv,.txt,.asc" ref={fileInputRef} data-testid="import-file-input" onChange={(e) => { setFile(e.target.files?.[0] || null); setPreview(null); setResult(null); }}
                            className="flex-1 text-xs text-slate-500 file:mr-3 file:rounded-[8px] file:border-0 file:bg-slate-100 dark:file:bg-white/10 file:px-3 file:py-2 file:text-xs file:font-bold file:text-slate-600 dark:file:text-slate-300" />
                        <button data-testid="import-preview-btn" onClick={doPreview} disabled={!file || busy} className="px-4 py-2 rounded-[8px] text-xs font-bold border border-slate-300 dark:border-white/15 hover:border-[#00B4D8] disabled:opacity-40">{tr("Preview")}</button>
                        <button data-testid="import-run-btn" onClick={doImport} disabled={!file || busy} className="brand-btn px-4 py-2 rounded-[8px] text-xs font-bold disabled:opacity-40 flex items-center gap-1.5">
                            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />} {tr("Run import")}
                        </button>
                    </div>
                    {preview && (
                        <div className="mt-3 rounded-[10px] border border-slate-200/70 dark:border-white/10 overflow-hidden" data-testid="import-preview">
                            <div className="px-3 py-2 bg-slate-50 dark:bg-white/5 text-[11px] font-bold text-slate-600 dark:text-slate-300 flex justify-between">
                                <span>{preview.total_rows} {tr("rows")} · {preview.rows_with_errors} {tr("with errors")}{preview.total_rows > preview.validated_rows ? ` ${tr("(first 50 checked)")}` : ""}</span>
                                {preview.extended_header && <span className="font-data text-[#B8860B]">${preview.extended_header.table} {preview.extended_header.pk}</span>}
                            </div>
                            <div className="overflow-x-auto max-h-56">
                                <table className="w-full text-[10px]">
                                    <thead><tr className="bg-slate-50/60 dark:bg-white/5">{current.columns.slice(0, 5).map((c) => <th key={c} className="px-2 py-1.5 text-left font-bold text-slate-400">{c}</th>)}<th className="px-2 py-1.5 text-left font-bold text-slate-400">{tr("status")}</th></tr></thead>
                                    <tbody>
                                        {preview.preview.map((r) => (
                                            <tr key={r.row} className="border-t border-slate-100 dark:border-white/5">
                                                {current.columns.slice(0, 5).map((c) => <td key={c} className="px-2 py-1.5 text-slate-600 dark:text-slate-300 truncate max-w-[140px]">{r.data[c]}</td>)}
                                                <td className={`px-2 py-1.5 font-bold ${r.errors.length ? "text-[#EF476F]" : "text-[#05A37E]"}`}>{r.errors.length ? r.errors.join("; ") : tr("OK")}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                    {result && (
                        <div className="mt-3 rounded-[10px] border border-[#06D6A0]/30 bg-[#06D6A0]/5 px-4 py-3 text-xs" data-testid="import-result">
                            <span className="font-bold text-[#05A37E]">{result.imported} {tr("imported")}</span>
                            <span className="text-slate-500"> · {result.skipped} {tr("skipped (duplicates)")} · {result.error_count} {tr("errors")}</span>
                            {result.errors?.slice(0, 3).map((e) => <div key={e.row} className="text-[10px] text-[#EF476F] mt-1">{tr("Row")} {e.row}: {e.errors.join("; ")}</div>)}
                        </div>
                    )}
                </div>
                <div>
                    <div className="rounded-[12px] border border-slate-200/70 dark:border-white/10 p-4 mb-4" data-testid="import-spatial">
                        <div className="font-heading font-bold text-sm text-navy dark:text-white flex items-center gap-2 mb-1"><MapPin className="w-4 h-4 text-[#EF476F]" /> {tr("Spatial data import (GeoJSON)")}</div>
                        <p className="text-[11px] text-slate-400 mb-3">{tr("Import parcel fabric, address points or street network layers — they render as toggleable map layers.")}</p>
                        <div className="flex items-center gap-2 flex-wrap">
                            <select data-testid="import-gis-layer" value={gisLayer} onChange={(e) => setGisLayer(e.target.value)}
                                className="rounded-[8px] border border-slate-300 dark:border-white/15 bg-background px-3 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-[#00B4D8]/40">
                                {spatialLayers.map((l) => <option key={l.key} value={l.key}>{l.label}</option>)}
                            </select>
                            <input type="file" accept=".geojson,.json" ref={gisInputRef} data-testid="import-gis-file" onChange={(e) => { setGisFile(e.target.files?.[0] || null); setGisResult(null); }}
                                className="flex-1 min-w-[140px] text-xs text-slate-500 file:mr-2 file:rounded-[8px] file:border-0 file:bg-slate-100 dark:file:bg-white/10 file:px-2.5 file:py-1.5 file:text-[11px] file:font-bold file:text-slate-600 dark:file:text-slate-300" />
                            <button data-testid="import-gis-btn" onClick={doSpatial} disabled={!gisFile || busy} className="brand-btn px-4 py-2 rounded-[8px] text-xs font-bold disabled:opacity-40">{tr("Import layer")}</button>
                        </div>
                        {gisResult && <div className="mt-2 text-[11px] font-bold text-[#05A37E]" data-testid="import-gis-result">{gisResult.imported} {tr("features imported to")} {gisResult.layer}</div>}
                    </div>
                    <div className="text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-2">{tr("Import history")}</div>
                    <div className="space-y-1.5 max-h-52 overflow-y-auto" data-testid="import-batches">
                        {batches.map((b) => (
                            <div key={b.id} className="flex items-center justify-between gap-2 text-[11px] px-3 py-2 rounded-[8px] bg-slate-50 dark:bg-white/5" data-testid={`import-batch-${b.id.slice(0, 8)}`}>
                                <span className="font-semibold text-slate-600 dark:text-slate-300">{b.target} · {b.filename}</span>
                                <span className="text-slate-400 font-data" title={b.filename}>{b.imported}/{b.total_rows}{b.errors ? ` · ${b.errors} err` : ""} · {String(b.created_at || "").slice(0, 10)}</span>
                            </div>
                        ))}
                        {!batches.length && <div className="text-[11px] text-slate-400">{tr("No imports yet.")}</div>}
                    </div>
                </div>
            </div>
        </div>
    );
}
